import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, Injector } from "@angular/core";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import {
  collection,
  collectionData,
  doc,
  docData,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  where
} from "@angular/fire/firestore";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { getAuth } from "firebase/auth";
import { BehaviorSubject, combineLatest, defer, EMPTY, firstValueFrom, from, Observable, of, ReplaySubject, Subject, Subscription, throwError, timer } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, take, takeUntil, tap, timeout, withLatestFrom, defaultIfEmpty, finalize } from "rxjs/operators";
import { StoreRepositoryService } from "src/app/pages/store/store-repository.service";
import { environment } from "src/environments/environment";
import { BaseService } from "../app.base.service";
import { CommonVariable } from "../app.constants";
import { RegProfileDialogComponentComponent } from "../components/reg-profile-dialog-component/reg-profile-dialog-component.component";
import { CommonService } from "./common/common.service";
import { MixpanelService } from "./mixpanel.service";
import { PaymentDataService } from "./payment-data.service";
import { RequestPaymentService } from "./RequestBodyService/RequestPayment/request-payment.service";
import { RequestProfileService } from "./RequestBodyService/RequestProfile/request-profile.service";
import { ToastPayload, ToastSeverity } from "@core/toast-facade.service";
import { ClarityService } from "./clarity.service";
import { PosthogService } from "./posthog.service";
import {
  FourCJourneyStateInput,
  FourCPaymentV2StateFields,
} from "../interfaces/sales-journey-state.i";
import { SalesJourneyIdentityService } from "./sales-journey-identity.service";
import {
  applyJoinBackendIdempotencyStatus,
  buildCreditJoinContextKey,
  cacheJoinSuccessResponse,
  clearInFlightJoinRequest,
  ensureJoinIdempotencyKey,
  getBrowserSessionStorage,
  getInFlightJoinRequest,
  interpretJoinBackendIdempotencyStatus,
  isCreditJoinResponseSuccess,
  isJoinCompletedForContext,
  markJoinCompletedForContext,
  readCachedJoinSuccessResponse,
  releaseJoinEndpointClaim,
  setInFlightJoinRequest,
  tryClaimJoinEndpointGuard,
} from "./syndicate-join-idempotency";

export interface ConfirmedPaidJourney {
  userId: string;
  prosubId: string;
  receiptId?: string;
  customerPurchaseId?: string;
  nextProsubId?: string;
}

interface CheckoutReceiptResume {
  userId: string;
  prosubId: string;
  logId: string;
  savedAt: number;
}

@Injectable({ providedIn: "root" })
export class PaymentService extends BaseService {

  private commonService = inject(CommonService);
  private catchService = inject(CatchErrorService);
  private posthogService = inject(PosthogService);

  userPaymentDetails$ = this.commonService.getUid("paymentService").pipe(
    switchMap((uid) =>
      combineLatest([
        this.paymentDataService.getUserSavedCardDetails(uid),
        this.authService.userDetails$,
      ])
    )
  );

  salesJournyPaymentDetails$ = new BehaviorSubject<any>(null);
  salesJourneyCustomerPurchaseId$ = new BehaviorSubject<string>(null);
  paymentSqlResp$ = new BehaviorSubject<any>(null);
  checkoutFlowUserId$ = new BehaviorSubject<string>(null);
  cashPurchaseCustomerPurchaseId$ = new BehaviorSubject<string>(null);
  ecryptionId$ = new BehaviorSubject<string>(null);
  cashPurchaseAddCardSyndicateId$ = new BehaviorSubject<string>(null);
  addOnOffersCustomerPurchaseIds$ = new BehaviorSubject<string[] | null>(null);
  private checkoutFlowUserIdSubject$ = new BehaviorSubject<string>(null);
  public getCustomerPurchaseDetails$ = new BehaviorSubject<Data | null>(null);

  private paidJourney: ConfirmedPaidJourney | null = null;
  private paidNextStep: { journey: ConfirmedPaidJourney; request: string; route: string } | null = null;

  /** In-memory continuation permission, recorded only by a confirmed response handler. */
  get confirmedPaidJourney(): ConfirmedPaidJourney | null {
    const user = getAuth().currentUser;
    if (!user || user.isAnonymous || user.uid !== this.paidJourney?.userId) return null;
    return this.paidJourney;
  }

  recordConfirmedPaidJourney(context: ConfirmedPaidJourney): void {
    const user = getAuth().currentUser;
    if (!user || user.isAnonymous || user.uid !== context.userId || !context.prosubId) return;
    this.paidJourney = { ...context };
    this.paidNextStep = null;
  }

  markConfirmedPaidJourneyNextOffer(baseProsubId: string, nextProsubId?: string): boolean {
    const context = this.confirmedPaidJourney;
    if (!context || String(context.prosubId) !== String(baseProsubId)) return false;
    context.nextProsubId = nextProsubId;
    return true;
  }

  clearConfirmedPaidJourney(): void {
    this.paidJourney = null;
    this.paidNextStep = null;
  }

  /** Persist only an opaque receipt pointer. Browser storage never establishes paid status. */
  rememberCheckoutReceipt(prosubId: string, logId: string): void {
    const user = getAuth().currentUser;
    if (!user || user.isAnonymous || !prosubId || typeof logId !== 'string' ||
        !logId.trim() || /[\/\r\n]/.test(logId) || logId === 'undefined') return;
    try {
      localStorage.setItem('checkoutReceiptResume', JSON.stringify({
        userId: user.uid, prosubId: String(prosubId), logId, savedAt: Date.now(),
      }));
    } catch { /* The live authenticated receipt stream still operates without storage. */ }
  }

  getCheckoutReceiptResume(prosubId: string): CheckoutReceiptResume | null {
    const user = getAuth().currentUser;
    if (!user || user.isAnonymous) return null;
    try {
      const resume = JSON.parse(localStorage.getItem('checkoutReceiptResume') || 'null');
      if (!resume || resume.userId !== user.uid || resume.prosubId !== String(prosubId) ||
          typeof resume.logId !== 'string' || !resume.logId.trim() || /[\/\r\n]/.test(resume.logId) || resume.logId === 'undefined' ||
          typeof resume.savedAt !== 'number' || resume.savedAt > Date.now() ||
          Date.now() - resume.savedAt > 24 * 60 * 60 * 1000) return null;
      return resume;
    } catch { return null; }
  }

  clearCheckoutReceiptResume(logId: string, ownerUserId = getAuth().currentUser?.uid): void {
    try {
      const resume = JSON.parse(localStorage.getItem('checkoutReceiptResume') || 'null');
      if (resume?.logId === logId && resume?.userId === ownerUserId) {
        localStorage.removeItem('checkoutReceiptResume');
      }
    } catch { /* Nothing to clear. */ }
  }

  /** Shared ceiling for the post-payment next-step decision. */
  static readonly POST_PAYMENT_NEXT_STEP_TIMEOUT_MS = 5000;

  /** Single 15s clock from ticket UI open through id resolution, base, and add-on load. */
  static readonly POST_PAYMENT_TICKET_DEADLINE_MS = 15000;
  private postPaymentTicketDeadlineAt: number | null = null;
  private postPaymentTicketsVisible = false;
  private readonly postPaymentDeadlineStarted$ = new BehaviorSubject<boolean>(false);
  private readonly postPaymentDeadlineReset$ = new Subject<void>();

  /** Clear leftover ticket IDs so a later MSN/payment does not reuse the first purchase. */
  resetPostPaymentTicketIds(): void {
    this.salesJournyPaymentDetails$.next(null);
    this.salesJourneyCustomerPurchaseId$.next(null);
    this.resetAncillaryPostPaymentTicketIds();
  }

  /** Clear add-on / cash-purchase IDs without touching primary MSN/CP subjects. */
  resetAncillaryPostPaymentTicketIds(): void {
    this.cashPurchaseCustomerPurchaseId$.next(null);
    this.cashPurchaseAddCardSyndicateId$.next(null);
    this.addOnOffersCustomerPurchaseIds$.next(null);
    this.getCustomerPurchaseDetails$.next(null);
  }

  resetPostPaymentTicketState(): void {
    this.resetPostPaymentTicketIds();
    this.postPaymentTicketDeadlineAt = null;
    this.postPaymentTicketsVisible = false;
    this.postPaymentDeadlineStarted$.next(false);
    this.postPaymentDeadlineReset$.next();
  }

  /** Starts once when the ticket UI opens. Must not restart when ids or products arrive. */
  startPostPaymentTicketDeadline(): void {
    if (this.postPaymentTicketDeadlineAt != null) {
      return;
    }
    this.postPaymentTicketDeadlineAt =
      Date.now() + PaymentService.POST_PAYMENT_TICKET_DEADLINE_MS;
    this.postPaymentDeadlineStarted$.next(true);
  }

  remainingPostPaymentTicketMs(): number {
    if (this.postPaymentTicketDeadlineAt == null) {
      return PaymentService.POST_PAYMENT_TICKET_DEADLINE_MS;
    }
    return Math.max(0, this.postPaymentTicketDeadlineAt - Date.now());
  }

  /**
   * Emits when the shared 15s deadline elapses.
   * Does not start the clock — call startPostPaymentTicketDeadline() first.
   * Reset cancels any in-flight timer so a stale timeout cannot fire after a new purchase flow.
   */
  postPaymentDeadline$(): Observable<void> {
    return this.postPaymentDeadlineStarted$.pipe(
      filter((started) => started),
      take(1),
      switchMap(() => timer(this.remainingPostPaymentTicketMs())),
      map(() => undefined),
      // Cancel waiters that subscribed before start, and in-flight timers after reset.
      takeUntil(this.postPaymentDeadlineReset$)
    );
  }

  markPostPaymentTicketsVisible(): void {
    this.postPaymentTicketsVisible = true;
  }

  arePostPaymentTicketsVisible(): boolean {
    return this.postPaymentTicketsVisible;
  }

  selected_p_type: any = "10";
  userProduct: any;
  freeRegJourneySuccessURL: string;
  freeRegJourneyStep: string;
  freeReg4cOrgProsub: string;
  userMsn: string;
  userStatus: string = "";
  checkOutComPayload$ = new BehaviorSubject<Data>({});
  jackpotPreAuthDone$ = new BehaviorSubject<any>(null);
  triggerActivatingData$ = new BehaviorSubject<any>({});
  preAuthActivateTriggerResponse$ = new BehaviorSubject<any>({});
  preferredCurrencyObj: any = null;
  salesParams: Data;
  currentNdcId$ = new BehaviorSubject<string>(null);
  private checkOutComDocId$ = new ReplaySubject<{ docId }>(1);
  public currentNdcId: string;
  private unsubscribe$ = new Subject<void>();
  private approvedMessageString = "approved";
  private succeededMessageString = "succeeded";
  private customerPurchaseSubscription: Subscription;
  private static readonly fourCDefaultToastId = "1026";
  private static readonly fourCSuccessToastId = "1166";
  /** Known Firebase toast_message doc ids for 4C checkout feedback (default + A–K). */
  private static readonly fourCKnownToastDocIds = new Set<string>([
    PaymentService.fourCDefaultToastId,
    PaymentService.fourCSuccessToastId,
    "1167",
    "1168",
    "1169",
    "1170",
    "1171",
    "1172",
    "1173",
    "1174",
    "1175",
    "1176",
  ]);

  constructor(
    public angularFireFunctions: AngularFireFunctions,
    private injector: Injector,
    private authService: AuthService,
    private http: HttpClient,
    private matDialog: MatDialog,
    private router: Router,
    private RequestPaymentService: RequestPaymentService,
    private RequestProfileService: RequestProfileService,
    private storeService: StoreRepositoryService,
    private paymentDataService: PaymentDataService,
    private mixPanelService: MixpanelService,
    private firestore: Firestore,
    private clarityService: ClarityService,
    private salesJourneyIdentityService: SalesJourneyIdentityService
  ) {
    super(injector);
  }

  /**
   * Business completion after a successful charge (sales journey + store cash/credit).
   * Clarity + PostHog `syndicate_created` (HATDEV-100772).
   */
  emitSyndicatePurchaseComplete(): void {
    this.clarityService.event("syndicate_purchase_complete");
    try {
      const encryptSyndicateId =
        this.salesJournyPaymentDetails$.value ||
        this.cashPurchaseAddCardSyndicateId$.value ||
        null;
      let userId: string | undefined;
      try {
        userId = getAuth()?.currentUser?.uid || undefined;
      } catch {
        userId = undefined;
      }
      if (userId) {
        this.posthogService.identify(userId);
      }
      this.posthogService.capture("syndicate_created", {
        c_customer_id: userId,
        encrypt_syndicate_id: encryptSyndicateId ? String(encryptSyndicateId) : undefined,
        source: "syndicate_purchase_complete",
      });
    } catch (error) {
      this.authService.errorHandlerLogs(
        "[PostHog] syndicate_created emit failed (non-blocking)",
        error
      );
    }
  }

  checkCreditBalance() {
    return this.commonService.getUid("PaymentService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);
        return this.authService.getAngularFireStore(
          `/users/${userId}/balance/current`,
          "get"
        );
      })
    );
  }


  doCreditPayment(offer_details) {
    try {
      const tp1 = this.salesParams.tp1 ? this.salesParams.tp1 : this.salesParams.TP1 ? this.salesParams.TP1 : "";
      const tp2 = this.salesParams.tp2 ? this.salesParams.tp2 : this.salesParams.TP2 ? this.salesParams.TP2 : "";
      const tp3 = this.salesParams?.tp3
        ? this.salesParams.tp3
        : this.salesParams?.TP3
          ? this.salesParams.TP3
          : this.salesParams[Object.keys(this.salesParams)?.find((key) => key?.toLowerCase() === "tp3")] || "";
      const t_id = this.salesParams?.t_id
        ? this.salesParams.t_id
        : this.salesParams?.T_ID
          ? this.salesParams.T_ID
          : this.salesParams[Object.keys(this.salesParams)?.find((key) => key?.toLowerCase() === "t_id")] || "";
      const other = this.salesParams.other
        ? this.salesParams.other
        : this.salesParams.Other
          ? this.salesParams.Other
          : "";
      return this.commonService.getUid("PaymentService").pipe(
        take(1),
        switchMap((userId) => {
          if (!userId) return throwError(() => new Error("User ID not found"));

          // Stable join idempotency key for buy_credit_offer_request — same key on retry/refresh.
          const storage = getBrowserSessionStorage();
          const contextKey = buildCreditJoinContextKey(userId, offer_details);
          const guard = tryClaimJoinEndpointGuard(storage, contextKey);
          const idempotencyKey = guard.idempotencyKey || ensureJoinIdempotencyKey(storage, contextKey);

          // Already completed for this offer context → replay the cached CF payload (do not re-charge).
          // Context key includes draw/line/product so a different selection is a new join.
          if (guard.alreadyCompleted || isJoinCompletedForContext(storage, contextKey)) {
            const cached = readCachedJoinSuccessResponse<Data>(storage, contextKey);
            if (isCreditJoinResponseSuccess(cached)) {
              return of({
                ...cached,
                response: { ...(cached as Data).response, idempotent_replay: true },
                idempotency_key: idempotencyKey,
              });
            }
            // Flag set without a usable cache (upgrade / storage loss) — fall through to CF
            // with the same idempotency_key so the backend can no-op and return real data.
          }

          // Coalesce concurrent duplicate clicks onto one in-flight CF call.
          const inFlight = getInFlightJoinRequest<Observable<Data>>(idempotencyKey);
          if (inFlight) {
            return inFlight;
          }

          // Refresh with a non-stale pending FE claim (!guard.claimed): do not hard-block —
          // fall through and probe backend key status via CF with the same idempotency_key
          // (completed→replay, processing→IN_PROGRESS, failed/missing→genuine retry).
          // Stale claims (>60s) are reclaimed inside tryClaimJoinEndpointGuard.
          // shouldProbeJoinIdempotencyBackendOnRefresh(storage, contextKey) === true on that path.

          const request$ = this.authService
            .getV2CloudFunction("buycreditofferrequestv2", {
              offer_details: offer_details,
              // Echo for CF routing / logging; module fields below are the authoritative payload.
              requestid: idempotencyKey,
              idempotency_key: idempotencyKey,
              modules: [
                {
                  website: "Lotto Social",
                  website_id: "27",
                  module_name: "buy_credit_offer_request",
                  user_id: userId, // ✅ from commonService
                  offer_id: offer_details.offerid,
                  visitor_id: null,
                  tp1,
                  tp2,
                  tp3,
                  t_id,
                  other,
                  // Backend join guard: repeated calls with the same key must be no-ops.
                  requestid: idempotencyKey,
                  idempotency_key: idempotencyKey,
                },
              ],
            })
            .pipe(
              tap((result: Data) => {
                const backendStatus = interpretJoinBackendIdempotencyStatus(result);
                if (backendStatus === "completed" && isCreditJoinResponseSuccess(result)) {
                  markJoinCompletedForContext(storage, contextKey);
                  cacheJoinSuccessResponse(storage, contextKey, result);
                }
                applyJoinBackendIdempotencyStatus(storage, contextKey, backendStatus);
              }),
              catchError((err) => {
                // Network / 5xx / thrown CF errors — unlock FE claim so refresh/retry can proceed.
                releaseJoinEndpointClaim(storage, contextKey);
                return throwError(() => err);
              }),
              finalize(() => clearInFlightJoinRequest(idempotencyKey)),
              shareReplay(1)
            );

          setInFlightJoinRequest(idempotencyKey, request$);
          return request$;
        })
      );
    } catch (err) {
      return throwError(() => err);
    }
  }


  makeCreditPayment(offerId) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.RequestPaymentService.getCreditPaymentRequest(offerId))
    );
  }

  // ✅ Updated insertCustomerPurchase
  insertCustomerPurchase(paymentDetails, document, one_click_pay = false) {
    let req$: Observable<any>;

    if (paymentDetails.isTopUp) {
      req$ = this.requestBodyService.validatePurchaseProductForTopUp(paymentDetails, document);
    } else if (paymentDetails?.isCreatingPS) {
      req$ = this.requestBodyService.validatePurchaseProductForCreatingPS(paymentDetails, document);
    } else {
      req$ = this.requestBodyService.validatePurchaseProduct(paymentDetails, document, one_click_pay);
    }

    return req$.pipe(
      switchMap((reqBody) => {
        console.log("[insertCustomerPurchase] built reqBody:", reqBody); // 🔍 Debug
        if (!reqBody) {
          return throwError(() => new Error('err_invalid_purchase_request'));
        }
        return this.authService.v2CloudFunctionResponse(this.callableFunction(reqBody));
      })
    );
  }

  insertCustomerPurchaseRequestJson(paymentDetails, document) {
    return this.requestBodyService.validatePurchaseProductJSON(paymentDetails, document).modules[0];
  }
  processPSCreatingCart(purchaseDetail: Data) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.requestBodyService.getPSCreatingCartRequest(purchaseDetail))
    );
  }

  processCardPayment(customerPurchaseId, card, document, canSubmit?: () => boolean, onNotInvoked?: () => void) {
    const owner = getAuth().currentUser;
    const ownerUserId = owner && !owner.isAnonymous ? owner.uid : null;
    const ownsPayment = () => !!ownerUserId && getAuth().currentUser?.uid === ownerUserId &&
      !getAuth().currentUser?.isAnonymous && canSubmit?.() !== false;
    return defer(() => {
      let invoked = false;
      return defer(() => {
        if (!ownsPayment()) return throwError(() => Object.assign(new Error('Payment owner changed before invocation'), {
          code: 'CALLABLE_NOT_INVOKED', reason: 'owner_changed',
        }));
        return this.requestBodyService.validateProcessPaymentRequest(customerPurchaseId, card, document).pipe(
          take(1),
          switchMap((res) => {
            if (!res) return throwError(() => new Error('err_invalid_process_payment_request'));
            const beforeInvoke = () => {
              if (!ownsPayment() || !Array.isArray(res.modules) || !res.modules.length ||
                  !res.modules.every(module => module.user_id === ownerUserId)) return false;
              // Auth's single-attempt transport has checked online status. From
              // this point a thrown/lost SDK response is an uncertain charge.
              invoked = true;
              return true;
            };
            return this.authService.getV2CloudFunction("apivalidateasyncv2", res, { retryAttempts: 0, beforeInvoke });
          }),
        );
      }).pipe(finalize(() => {
        // Unsubscribing during the asynchronous UID/payload wait proves that no
        // charge call left. Release only the caller's exact unsubmitted pointer.
        if (!invoked) onNotInvoked?.();
      }));
    });
  }

  public payForTopUp(paymentDetails: any) {
    return this.requestBodyService
      .validateProcessPaymentRequestForTopUp(paymentDetails)
      .pipe(
        switchMap((payload) => {
          if (!payload) {
            return of(null);
          }

          return this.authService?.v2CloudFunctionResponse(
            this.callableFunction(payload)
          );
        }),
        tap({
          error: (err) => this.authService.errorHandlerLogs("[payForTopUp] Cloud Function Error:", err),
        })
      );
  }

  // credit refresh charge request functionality
  getCreditRefreshCharge() {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.requestBodyService.getCreditRefreshRequest())
    );
  }

  getCardAndUserDetails(offerId, orderId?) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(this.RequestPaymentService.getUserAndCardDetailsRequests(offerId, orderId))
    );
  }

  getUserCardDetails(offerId, offerPrize) {
    return this.getCardAndUserDetails(offerPrize);
  }

  makePaymentWithPayMilGateway(offerDetails, customerDetails, cardDetails, purchaseId) {
    return this.authService?.v2CloudFunctionResponse(
      this.callableFunction(
        this.RequestPaymentService.getPayMilFinalPaymentRequest(offerDetails, customerDetails, cardDetails, purchaseId)
      )
    );
  }

  createSyndicateRequest(offerDetails: any): Promise<number> {
    const id = Math.floor(new Date().getUTCMilliseconds() * Math.floor(new Date().getMilliseconds()));
    if (!id) return Promise.reject("Invalid ID");

    // Ensure all properties are defined
    for (const key in offerDetails) {
      if (offerDetails[key] === undefined) {
        return Promise.reject(`Property ${key} is undefined`);
      }
    }

    // Get userId from commonService
    return this.commonService.getUid("SyndicateService").pipe(take(1)).toPromise().then((userId) => {
      if (!userId) return Promise.reject("User ID not found");

      const path_id = `0haUUTTTnlt2S0q72aYj`;
      const doc_data = offerDetails;
      const dv = { docId: id };
      const method = "update"; // or "update" if you want to merge

      return new Promise<number>((resolve, reject) => {
        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).subscribe({
          next: () => resolve(id), // return generated ID
          error: (err) => reject(err),
        });
      });
    });
  }
  updateSyndicateRequest(offerDetails: any, id: any) {
    if (!id) return;

    // Ensure all properties are defined
    for (const key in offerDetails) {
      if (offerDetails[key] === undefined) {
        return;
      }
    }

    return this.commonService.getUid("SyndicateService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        const path_id = `0haUUTTTnlt2S0q72aYj`;
        const doc_data = offerDetails;
        const method = "update";

        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).pipe(take(1));
      })
    );
  }

  getSyndicateInfoWithProducts(syndicateId): Observable<any> {
    if (!syndicateId) {
      this.authService.errorHandlerLogs('getSyndicateInfoWithProducts: Invalid syndicate ID');
      return EMPTY;
    }
    return combineLatest([
      this.getSyndicateInfo(syndicateId),
      this.getSyndicateProdcuts(syndicateId)
    ]).pipe(
      map(([syndicateInfo, syndicateProducts] :any) => {
        // Validate that both results are present
        if (!syndicateInfo || !syndicateProducts) {
          console.warn('getSyndicateInfoWithProducts: Missing data', { syndicateInfo, syndicateProducts });
          return null;
        }

        // Check if syndicateInfo is a placeholder - don't emit
        if (syndicateInfo.isPlaceholder) {
          console.warn('getSyndicateInfoWithProducts: Received placeholder syndicateInfo');
          return null;
        }

        // Validate syndicateInfo has a data function
        if (typeof syndicateInfo.data !== 'function') {
          console.warn('getSyndicateInfoWithProducts: syndicateInfo.data is not a function', syndicateInfo);
          return null;
        }

        // Return the valid array structure
        return [syndicateInfo, syndicateProducts];
      }),
      filter((result) => result !== null),
      catchError((error) => {
        console.error('Error fetching syndicate info with products:', error);
        // Return EMPTY to prevent emitting partial data that causes downstream errors
        return EMPTY;
      })
    );
  }

  getSyndicateDetails(syndicateId: string) {
    return this.commonService.getUid("SyndicateService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of({}); // Handle missing userId

        return this.authService
          .getAngularFireStore(
            `/users/${userId}/userSyndicate_list/${syndicateId}`,
            "snapshotChanges"
          )
          .pipe(
            map((res: any) => res?.payload?.data()),
            catchError(() => of({})),
            take(1)
          );
      })
    );
  }


  getPrivateSyndicateInfo(syndicate_id: string) {
    return this.commonService.getUid("SyndicateService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null); // Return null if no userId

        const userSyndicateCollectionRef = collection(
          this.firestore,
          `/users/${userId}/userSyndicate_list`
        );
        const userSyndicateQuery = query(userSyndicateCollectionRef, orderBy("creation_date", "desc"));
        const syndicateDocRef = doc(userSyndicateCollectionRef, syndicate_id);

        return this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return null;
            const docSnapshot = await getDoc(syndicateDocRef);
            return docSnapshot.exists() ? docSnapshot.data() : null;
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      })
    );
  }

  getCustomerPurchaseDetailsFromSQL(ndcId: any) {
    return this.commonService.getUid("paymentService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        const customerPurchaseRef = collection(
          this.firestore,
          `users/${userId}/customer_purchase`
        );
        const customerPurchaseQuery = query(customerPurchaseRef, where("ndc", "==", ndcId));

        return this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return null;

            const querySnapshot = await getDocs(customerPurchaseQuery);
            const data = querySnapshot.docs.map((doc) => doc.data());
            return data.length > 0 ? data[0] : null;
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      })
    );
  }

  getSyndicateInfoAddCard(syndicateId: any) {
    return this.commonService.getUid("SyndicateService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        const userRef = doc(this.firestore, `users/${userId}`); // Use userId from commonService
        const managedTicketsRef = collection(userRef, "managed_tickets");
        const syndicateDocRef = doc(managedTicketsRef, syndicateId);

        return this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return null;

            const docSnapshot = await getDoc(syndicateDocRef);
            return docSnapshot.exists() ? docSnapshot.data() : null;
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      })
    );
  }


  getSyndicateInfo(syndicateId: any) {
    return this.commonService.getUid("SyndicateService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) {
          console.warn('getSyndicateInfo: No userId - returning placeholder');
          return of({
            data: () => ({}),
            exists: () => false,
            id: 'no-user-placeholder',
            isPlaceholder: true
          });
        }

        const userRef = doc(this.firestore, `users/${userId}`);
        const managedTicketsRef = collection(userRef, "managed_tickets");
        const syndicateDocRef = doc(managedTicketsRef, syndicateId);

        return this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) {
              console.warn('getSyndicateInfo: Offline - returning placeholder');
              return {
                data: () => ({}),
                exists: () => false,
                id: 'offline-placeholder',
                offline: true,
                isPlaceholder: true
              };
            }

            try {
              const docSnapshot = await getDoc(syndicateDocRef);
              return docSnapshot.exists() ? docSnapshot : {
                data: () => ({}),
                exists: () => false,
                id: 'not-found-placeholder',
                isPlaceholder: true
              };
            } catch (error) {
              console.error('Error fetching syndicate info:', error);
              return {
                data: () => ({}),
                exists: () => false,
                id: 'error-placeholder',
                isPlaceholder: true
              };
            }
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          catchError((error) => {
            console.error('getSyndicateInfo error:', error);
            return of({
              data: () => ({}),
              exists: () => false,
              id: 'catch-error-placeholder',
              isPlaceholder: true
            });
          }),
          shareReplay(1)
        );
      })
    );
  }

  getSyndicateProdcutsAddCard(syndicateId: any) {
    if (!syndicateId) return of(null);

    return this.commonService.getUid("SyndicateService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        const userRef = doc(this.firestore, `users/${userId}`);
        const managedTicketsRef = collection(userRef, "managed_tickets");
        const syndicateDocRef = doc(managedTicketsRef, syndicateId);
        const productsCollectionRef = collection(syndicateDocRef, "products");
        const productsQuery = query(productsCollectionRef, orderBy("product_name", "desc"));

        return this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return null;

            const querySnapshot = await getDocs(productsQuery);
            return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      })
    );
  }


  getSyndicateProdcuts(syndicateId: any) {
    if (!syndicateId) {
      console.warn('getSyndicateProdcuts: No syndicateId - returning empty docs');
      return of({ docs: [], isEmpty: true });
    }

    return this.commonService.getUid("SyndicateService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) {
          console.warn('getSyndicateProdcuts: No userId - returning empty docs');
          return of({ docs: [], isEmpty: true });
        }

        const userRef = doc(this.firestore, `users/${userId}`);
        const managedTicketsRef = collection(userRef, "managed_tickets");
        const syndicateDocRef = doc(managedTicketsRef, syndicateId);
        const productsCollectionRef = collection(syndicateDocRef, "products");
        const productsQuery = query(productsCollectionRef, orderBy("product_name", "desc"));

        return this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) {
              console.warn('getSyndicateProdcuts: Offline - returning empty docs');
              return { docs: [], isEmpty: true, offline: true };
            }

            try {
              const querySnapshot = await getDocs(productsQuery);
              return querySnapshot;
            } catch (error) {
              console.error('Error fetching syndicate products:', error);
              return { docs: [], isEmpty: true, error: true };
            }
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          catchError((error) => {
            console.error('getSyndicateProdcuts error:', error);
            return of({ docs: [], isEmpty: true, error: true });
          }),
          shareReplay(1)
        );
      })
    );
  }

  getCheckoutId(amount: string, currency: string, cp_request: Data = {}, paymentReason = "") {
    try {
      const prosubId = this.userProduct?.prosub_id;
      const truevoId = "22";
      const truevoDetails = this.authService?.truevoDetails$?.value;

      if (truevoDetails && truevoDetails?.prosubs?.includes(prosubId)) {
        this.selected_p_type = truevoId;
      }

      return this.commonService.getUid("PaymentService").pipe(
        take(1),
        switchMap((userId) => {
          const auth = getAuth();
          const anonymousId = auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null;

          if (!userId && !anonymousId) return of(null);

          return this.authService.userDetails$.pipe(
            take(1),
            switchMap((user: Data) => {
              this.userMsn = user?.msn;
              const preferredCurrencyObject = this.preferredCurrencyObj ? this.preferredCurrencyObj : "";

              const checkoutPayload = {
                ...cp_request,
                p_type: this.selected_p_type,
                amount: amount,
                currency: currency,
                ip: CommonVariable.userIP,
                mobile: this.userMsn,
                status: this.userStatus ? this.userStatus : "NEW",
                preferred_currency: preferredCurrencyObject,
                anonymous_id: anonymousId,
                merchantCustomerId: userId || anonymousId,
                payment_reason: paymentReason,
              };
              return this.authService.getV2CloudFunction(
                environment.cloudFunctions.getCheckoutId,
                this.salesJourneyIdentityService.applyIdentityEnvelope(checkoutPayload, {
                  userId: userId || null,
                  anonymousId,
                  // Omit customerId: resolve the actual SQL ID when available.
                  msn: this.userMsn,
                })
              );
            })
          );
        })
      );
    } catch (error) {
      return of({
        success: false,
        code: "Error calling getcheckoutidv2",
        message: "Error calling getcheckoutidv2",
      });
    }
  }


  getCheckoutIdDev(amount: string, currency: string, cp_request: Data = {}) {
    console.trace();
    this.authService.userDetails$.pipe(take(1)).subscribe((user: Data) => {
      this.userMsn = user?.msn;
    });
    return this.angularFireFunctions
      .httpsCallable("getcheckoutiddev")({
        ...cp_request,
        p_type: this.selected_p_type,
        amount: amount,
        currency: currency,
        ip: CommonVariable.userIP,
        mobile: this.userMsn,
        status: this.userStatus ? this.userStatus : "NEW",
      })
      .pipe(
        timeout(10000),
        catchError((err) => {
          return of(err);
        })
      );
  }

  checkPaymentStatus(cId: string, paymentMode?: string) {
    const cf = paymentMode === "sandbox" ? "getpaymentstatusdev" : environment.cloudFunctions.getPaymentStatus;
    return this.authService?.getV2CloudFunction(cf, {
      p_type: this.selected_p_type,
      checkoutId: cId,
    });
  }
  // calling the apiSync cloude functions and return the value
  checkPaymentStatusViaSQL(ndc: string) {
    // V2
    return this.authService?.getV2CloudFunction(
      "apisyncv2",
      this.RequestPaymentService?.getPaymentStatusViaSQLRequest(ndc)
    );
    // V1
    //  const api = this.fns.httpsCallable("apiSync");
    //  return api(this.requestBodyService.getPaymentStatusViaSQLRequest(ndc));
  }

  checkPaymentStatusDev(cId: string) {
    return this.angularFireFunctions.httpsCallable("getpaymentstatusdev")({
      p_type: this.selected_p_type,
      checkoutId: cId,
    });
  }

  updatePaymentReference(customerPurchaseId, paymentDocId) {
    // return this.angularFireFunctions.httpsCallable("updatePaymentReference")({
    //   customer_purchase_id: customerPurchaseId,
    //   payment_id: paymentDocId,
    // })
    return this.authService.getV2CloudFunction("updatepaymentreferencev2", {
      customer_purchase_id: customerPurchaseId,
      payment_id: paymentDocId,
    });
  }

  updatePaymentReqAndRes(req: Data, res: Data, preAuth?: any) {
    return this.authService?.getV2CloudFunction(
      "apiv2",
      this.RequestPaymentService.getPaymentReqResRequest(req, res, preAuth)
    );
  }

  updatePaymentReqAndResNew(req: Data, res: Data, cp_Request: Data, ndc: string, preAuth: boolean) {
    return this.authService?.getV2CloudFunction(
      "apiv2",
      this.RequestPaymentService.getPaymentReqResRequestNew(req, res, cp_Request, ndc, preAuth)
    );
  }


  async getOptionalPage(
    free_reg_journey: string,
    deadlineAt = Date.now() + PaymentService.POST_PAYMENT_NEXT_STEP_TIMEOUT_MS,
    isCurrent?: () => boolean,
  ): Promise<string> {
    const isEmpty = (val: any) => val === null || !(Object.keys(val || {})?.length);
    const journey = this.confirmedPaidJourney;

    try {
      if (isCurrent?.() === false) return "free-reg-complete-profile";
      if (journey && this.paidNextStep?.journey === journey && this.paidNextStep.request === free_reg_journey) {
        return this.paidNextStep.route;
      }
      await this.readNextStepValue(this.commonService.getUid("OptionalPageService"), deadlineAt);
      if (isCurrent?.() === false) return "free-reg-complete-profile";
      const prosubId = this.checkoutRouteProsubId(this.userProduct?.prosub_id ?? localStorage.getItem("prosub_id"));
      if (!prosubId) return "free-reg-complete-profile";
      const freeRegjourney = free_reg_journey;
      const altProsubId = "";
      let post_name = "free-reg-complete-profile";

      const ref = await this.readNextStepValue<Data>(
        this.authService.getAngularFireStore(`/general_modules/prosub_id/${prosubId}/details`, "get"),
        deadlineAt,
      );

      if (isCurrent?.() === false) return "free-reg-complete-profile";
      if (ref?.exists && !isEmpty(ref?.data())) {
        const prosubDocRefData = ref.data();
        const addonProsubId = this.checkoutRouteProsubId(prosubDocRefData?.addon_prosub_id);
        if (!addonProsubId || addonProsubId === prosubId) return "free-reg-complete-profile";
        const showPreAuth = prosubDocRefData?.show_pre_auth;

        // £0 card verification joins must not chase addon / alternate 4c upsells
        // (e.g. 4861 -> profile -> addon 3025 -> profile).
        if (prosubDocRefData?.card_verification === true) {
          post_name = "free-reg-complete-profile";
        } else if (freeRegjourney === "pre-auth-4c") {
          post_name = await this.getOptionalPageOfAddon(addonProsubId, 0, deadlineAt, isCurrent);
        } else if (addonProsubId) {
          const optionalPageName = await this.getOptionalPageOfAddon(addonProsubId, showPreAuth, deadlineAt, isCurrent);
          if (isCurrent?.() === false) return "free-reg-complete-profile";
          if (free_reg_journey === "no-thanks") {
            const cardData = await this.readNextStepValue(this.authService.$CardData, deadlineAt);
            if (isCurrent?.() === false) return "free-reg-complete-profile";
            post_name = cardData?.length ? optionalPageName : "free-reg-complete-profile";
          } else {
            // The addon resolver already carries its prosub query. Paid navigation
            // does not depend on stored-card delivery and must not wait for it.
            post_name = optionalPageName;
          }
        } else if (altProsubId !== "") {
          post_name = await this.getOptionalPageOfAddon(altProsubId, 0, deadlineAt, isCurrent);
        }
      }

      if (isCurrent?.() === false) return "free-reg-complete-profile";
      // A successful route decision may consume the existing savedCards flag.
      // A presentation retry must reuse that decision for this exact confirmed
      // purchase, rather than resolve it again with different one-shot inputs.
      // Transient/unknown profile fallbacks are deliberately not cached.
      if (journey && this.confirmedPaidJourney === journey && post_name !== "free-reg-complete-profile") {
        this.paidNextStep = { journey, request: free_reg_journey, route: post_name };
      }
      return post_name;
    } catch (error) {
      console.error("Error in getOptionalPage:", error);
      return "free-reg-complete-profile";
    }
  }

  async getOptionalPageOfAddon(
    addonProsubId: any,
    showPreAuth: any,
    deadlineAt = Date.now() + PaymentService.POST_PAYMENT_NEXT_STEP_TIMEOUT_MS,
    isCurrent?: () => boolean,
  ): Promise<string> {
    const fallback = "free-reg-complete-profile";
    try {
      if (isCurrent?.() === false) return fallback;
      addonProsubId = this.checkoutRouteProsubId(addonProsubId);
      const currentProsubId = this.checkoutRouteProsubId(this.userProduct?.prosub_id ?? localStorage.getItem("prosub_id"));
      if (!addonProsubId || addonProsubId === currentProsubId) return fallback;
      const ref = await this.readNextStepValue<Data>(
        this.authService.getAngularFireStore(`/general_modules/prosub_id/${addonProsubId}/details`, "get"),
        deadlineAt,
      );
      if (isCurrent?.() === false) return fallback;
      const data = ref?.exists ? ref.data() : null;
      const optionalPage = typeof data?.optional_page === "string" ? data.optional_page.trim().replace(/^\//, "") : "";
      if (!/^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/.test(optionalPage)) return fallback;
      const [currentPath, currentQuery = ""] = (this.router?.url ?? "").split("?");
      const currentParams = new URLSearchParams(currentQuery.split("#")[0]);
      const routeProsubId = this.checkoutRouteProsubId(currentParams.get("prosub_id") ?? currentParams.get("Prosub_ID"));
      if (currentPath.replace(/^\//, "") === optionalPage && routeProsubId === addonProsubId) return fallback;

      const configuredPage = `${optionalPage}?prosub_id=${encodeURIComponent(addonProsubId)}`;
      if (localStorage.getItem("savedCards") !== null) {
        if (isCurrent?.() === false) return fallback;
        localStorage.removeItem("savedCards");
        return configuredPage;
      }
      // A profile is needed only for a pre-auth decision. Waiting after the document
      // arrives avoids withLatestFrom discarding a one-shot read before auth emits.
      if (showPreAuth) {
        const user = await this.readNextStepValue<Data | null>(this.authService.userDetails$, deadlineAt);
        if (isCurrent?.() === false || user === undefined) return fallback;
        if (!user?.token_exists && !user?.free_reg_4h_std_mode) {
          return `pre-auth?prosub_id=${addonProsubId}`;
        }
      }
      return isCurrent?.() === false ? fallback : configuredPage;
    } catch (error) {
      this.authService.errorHandlerLogs("Error resolving next checkout step:", error);
      return fallback;
    }
  }

  private checkoutRouteProsubId(value: unknown): string | null {
    if (typeof value !== "string" && typeof value !== "number") return null;
    if (typeof value === "number" && !Number.isFinite(value)) return null;
    const id = String(value).trim();
    return /^[a-zA-Z0-9_-]+$/.test(id) ? id : null;
  }

  /** All next-page prerequisites consume one shared deadline; empty reads settle. */
  private readNextStepValue<T>(source: Observable<T>, deadlineAt: number): Promise<T | undefined> {
    return firstValueFrom(source.pipe(
      take(1),
      timeout({ first: Math.max(0, deadlineAt - Date.now()) }),
      defaultIfEmpty(undefined),
    ));
  }
  processcustomer_purchase(customerPurchaseId, prosub_id, offer_id) {
    return this.RequestProfileService.customer_purchase_capping(customerPurchaseId, prosub_id, offer_id).pipe(
      switchMap((payload) => {
        if (!payload) {
          return of(null);
        }
        return this.authService.v2CloudFunctionResponse(
          this.callableFunction(payload)
        );
      })
    );
  }

  public updateCustomerPurchaseWithNdc(
    customerPurchaseId: string,
    ndc: string
  ): Observable<any> {
    return this.RequestPaymentService.validatePurchaseProductWithNdc(customerPurchaseId, ndc).pipe(
      filter((req) => req !== null),
      switchMap((req) =>
        this.authService.getV2CloudFunction("apiv2", req)
      )
    );
  }

  setCurrentNdcId(ndcId) {
    this.currentNdcId$.next(ndcId);
  }

  getCurrentNdcId(): Observable<string> {
    return this.currentNdcId$.asObservable();
  }

  getPTypeByPreferredCurrency(preferredCurrency: string) {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return of([]); // Fallback when offline

        const colRef = collection(this.firestore, "general_modules/p_type/list");
        const q = query(colRef, where("valid_currency", "array-contains", preferredCurrency));

        return from(getDocs(q)).pipe(
          map((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    );
  }

  getPTypeByOffer(pType) {
    return this.authService.getAngularFireStore(`/general_modules/p_type/list/${pType}`, "get");
  }

  private snapShotData(actions: any[]): Data[] {
    // Add type guard to ensure actions is an array before processing
    if (!Array.isArray(actions)) {
      this.authService.errorHandlerLogs('snapShotData: Input is not an array, returning empty array', { inputType: typeof actions });
      return [];
    }
    
    return (actions ?? [])
      .filter((a) => a?.payload?.doc) // Ensure doc exists
      .map((a) => {
        const data = a.payload.doc.data() as Data;
        const id = a.payload.doc.id;
        return { id, ...data };
      });
  }

  getCheckOutComResponse$ = combineLatest([
    this.checkOutComDocId$,
    this.commonService.getUid("CheckOutComService"),
    this.catchService.isOnline$,
    this.checkoutFlowUserId$
  ]).pipe(
    switchMap(([{ docId }, userId, isOnline, checkoutFlowUserId ]) => {
      if (!docId || !userId || !checkoutFlowUserId ) {
        // No docId or userId -> emit empty
        return of([]);
      }
      if (!isOnline) {
        return EMPTY;
      }
    
      const UserID = userId  ?? checkoutFlowUserId;
      // ✅ Build Firestore reference once all values available
      const checkoutDocRef = doc(
        this.firestore,
        `users/${UserID}/checkoutcom/${docId}`,
      );

      return docData(checkoutDocRef).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  setcheckOutComDocId(checkOutComDocId) {
    this.checkOutComDocId$.next(checkOutComDocId);
  }

  /**
   * Point the Firestore listener at checkoutcom/{logId} and, for frictionless Flow
   * payments, invoke handlecheckoutresponsev3 so the backend fulfils the purchase.
   */
  syncCheckoutFlowStatus(
    params:
      | string
      | {
          logId: string;
          paymentId?: string;
          userId?: string;
          sandbox?: boolean;
        },
  ): void {
    const resolved =
      typeof params === "string"
        ? { logId: params }
        : params;

    this.setcheckOutComDocId({ docId: String(resolved.logId) });

    if (!resolved.paymentId) {
      return;
    }

    const userId$ = resolved.userId
      ? of(resolved.userId)
      : this.commonService.getUid("syncCheckoutFlowStatus");

    userId$.pipe(take(1)).subscribe((userId) => {
      if (!userId) {
        return;
      }

      this.checkoutFlowUserId$.next(userId);

      const functionName = resolved.sandbox
        ? "createcheckoutflowsessionv2"
        : environment.cloudFunctions.createCheckoutFlowSession;
      const baseUrl =
        `https://${AuthService.FIREBASE_REGION}-hatchster-firebase-project.cloudfunctions.net/${functionName}`;
      const sandboxQuery = resolved.sandbox ? "&sandbox=true" : "";
      const url =
        `${baseUrl}?status=success` +
        `&logId=${encodeURIComponent(resolved.logId)}` +
        `&userId=${encodeURIComponent(userId)}` +
        `&cko-payment-id=${encodeURIComponent(resolved.paymentId)}` +
        sandboxQuery;

      this.http
        .get(url, { responseType: "text" })
        .pipe(
          catchError((error) => {
            this.authService.errorHandlerLogs("handlecheckoutresponsev3 failed", error);
            return of(null);
          }),
        )
        .subscribe();
    });
  }

  getCheckoutApiDetails(queryParam): Observable<{ collection: string; data: Data }> {
    const paymentMode = queryParam?.pt || null;
    const credentials = paymentMode === "sandbox" ? "credentials_sb" : environment.credentials;
    const docRef = doc(this.firestore, `general_modules/p_type/list/21`); // Reference Firestore document
    return from(getDoc(docRef)).pipe(
      map((docSnap: any) => {
        const docData = docSnap?.data() ?? {};
        const credentialsData = docData?.[credentials] || docData;
        const processingChannelId =
          credentialsData?.processing_channel_id ??
          credentialsData?.processingChannelId ??
          docData?.credentials?.processing_channel_id ??
          docData?.credentials_sb?.processing_channel_id ??
          "";

        return {
          collection: "checkOutApiDetails",
          data: {
            ...credentialsData,
            processing_channel_id: processingChannelId,
          },
        };
      }),
      catchError((error) => {
        return throwError(() => error); // Handle errors gracefully
      })
    );
  }
  async checkOutPayment(paymentData, secretKey) {
    try {
      const response: Data = await this.http
        .post("https://api.sandbox.checkout.com/payments", paymentData, {
          headers: new HttpHeaders({
            Authorization: `Bearer ${secretKey}`,
            "Content-Type": "application/json",
          }),
        })
        .toPromise();
      if (response?.approved) {
        this.getcheckoutPaymentError("1116");
      } else {
        this.getcheckoutPaymentError("1117");
      }
    } catch (error) {
      this.getcheckoutPaymentError("1117");
    }
  }

  getcheckoutPaymentError(code: string) {
    if (code == "1116") {
      this.authService
        .getConstantsMsg("1116")
        .then((translatedMsg) => {
          const defaultMsg = {
            message: {
              line_1: "Payment successful",
              line_2: "",
            },
            style: {
              icon: "tick circle success.png",
              type: "Success",
            },
          };
          this.authService.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          return error;
        });
    } else {
      this.authService
        .getConstantsMsg("1117")
        .then((translatedMsg) => {
          const defaultMsg = {
            message: {
              line_1: "Payment failed",
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };
          this.authService.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          return error;
        });
    }
  }

  getCustomerPurchaseDetailsFromEncryptedCpId(cpId: string) {
    return this.commonService.getUid("CustomerPurchaseService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);

        const userRef = doc(this.firestore, `users/${userId}`);
        const customerPurchaseRef = collection(userRef, "customer_purchase");
        const cpDocRef = doc(customerPurchaseRef, cpId);

        return this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return null;

            const docSnapshot = await getDoc(cpDocRef);
            return docSnapshot.exists() ? docSnapshot.data() : null;
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      shareReplay(1)
    );
  }
  // Get Customer Purchase Data from firebase store
  getCustomerPurchaseData$ = this.commonService.getUid("CustomerPurchaseService").pipe(
    switchMap((userId) => {
      if (!userId) return of(null);

      return this.catchService.isOnline$.pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of(null);

          const customerPurchaseRef = collection(
            this.firestore,
            `users/${userId}/customer_purchase`
          );
          const customerPurchaseQuery = query(
            customerPurchaseRef,
            orderBy("timestamp_creation", "desc")
          );

          return from(getDocs(customerPurchaseQuery)).pipe(
            map((querySnapshot) => {
              const data = querySnapshot.docs.map((doc) => doc.data());
              return data.length > 0 ? data[0] : null;
            })
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError
      );
    }),
    shareReplay(1)
  );


  // Getting data using unique Upsell id
  getUpsellList(upsell_ID: string) {
    // Return empty observable if upsell_ID is missing/invalid
    if (!upsell_ID) {
      return of(null);
    }

    try {
      return this.authService
        .getAngularFireStore(`/general_modules/upsell_lists/h6UhAn9HuwwGFIkNowKZ/${upsell_ID}`, "snapshotChanges")
        .pipe(
          map((res: Data) => {
            return res?.payload?.data();
          }),
          catchError((error) => {
            // Always return an Observable, never undefined
            console.error('Error fetching upsell list:', error);
            return of(null);
          }),
          shareReplay(1)
        );
    } catch (error) {
      // Return of(null) instead of error to ensure Observable is always returned
      console.error('Error in getUpsellList:', error);
      return of(null);
    }
  }

  /**
   * The trackCustomerPurchase function tracks the purchase process for a customer and handles timeouts
   * and responses accordingly.
   */
  trackCustomerPurchase(ndc: string) {
    let ticketTimer = 0;
    this.getSyndicateIdFromCustomerPurchase(ndc);
    let customerPurchaseInt = setInterval(() => {
      this.getSyndicateIdFromCustomerPurchase(ndc)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((response) => {
          ticketTimer++;
          if (response == null && ticketTimer >= CommonVariable.syndicateCreationTimeOut) {
            const defaultMsg = {
              message: {
                line_1: "We are still creating your ticket, don't worry we got this.",
                line_2: "",
              },
              style: {
                icon: "tick circle success.png",
                type: "Success",
              },
            };
            this.authService.showToastMessage({}, defaultMsg);
            clearInterval(customerPurchaseInt);
            this.router.navigate(["mainTabs/tabs/syndicate"]);
          }

          if (response && response !== null) {
            clearInterval(customerPurchaseInt);
            this.salesJourneyCustomerPurchaseId$.next(response);
          }
        });
    }, 2000);
  }

  /*
  - Retrieves the syndicate ID from a customer's purchase data based on the provided NDC.
  - Queries Firestore to find the matching purchase and extracts the syndicate ID if found.
    */
  getSyndicateIdFromCustomerPurchase(ndc: string): Observable<string | null> {
    return this.commonService.getUid("CustomerPurchaseService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);

        const customerPurchaseCollectionRef = collection(
          this.firestore,
          `users/${userId}/customer_purchase`
        );
        const customerPurchaseQuery = query(
          customerPurchaseCollectionRef,
          where("ndc", "==", ndc)
        );

        return from(getDocs(customerPurchaseQuery)).pipe(
          map((res) => {
            if (!res.empty) {
              const foundResult: any[] = [];
              res.forEach((f) => {
                foundResult.push({ ...f.data(), id: f.id });
              });
              const cp_response = foundResult[0] as Data;
              return cp_response?.id ?? null;
            }
            return null;
          })
        );
      })
    );
  }


  /**
   * The function `fetchSyndicateAfterPayment` fetches syndicate information after a payment is made and
   * displays a ticket using the provided callback function.
   */
  fetchSyndicateAfterPayment(syndicateId, showTicket: (Data) => void) {
    let sqlAPICall;
    let paymentSqlResp = this.paymentSqlResp$.value;

    if (syndicateId) {
      sqlAPICall = syndicateId;
    } else if (this.salesJournyPaymentDetails$.value) {
      sqlAPICall = this.salesJournyPaymentDetails$.value;
    } else if (!paymentSqlResp) {
      this.matDialog.open(RegProfileDialogComponentComponent, {
        panelClass: "ongoingWarning-size",
        backdropClass: "ongoingWarning-back-drop-color",
        disableClose: true,
      });
      return;
    } else if (paymentSqlResp?.response?.status !== "SUCCESS") {
      this.authService.getConstantsMsg("1046").then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "There is an issue with syndicate creation process",
            line_2: "",
          },
          style: {
            type: "Error",
            icon: "tick circle error.svg",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      });
      return;
    } else {
      sqlAPICall = paymentSqlResp?.response?.encrypt_syndicate_id;
    }
    if (sqlAPICall) {
      this.getSyndicateInfoWithProducts(sqlAPICall)
        .pipe(
          takeUntil(this.unsubscribe$),
          map((detail: Data) => {
            if (!detail?.[0] || typeof detail[0]?.data !== 'function') return;
            const syndicateInfo = detail[0].data();
            if (!syndicateInfo) return;
            syndicateInfo.product = [];
            const productDocs = detail[1]?.docs;
            if (productDocs) {
              for (let index = 0; index < productDocs.length; index++) {
                syndicateInfo?.product.push(productDocs[index]?.data());
              }
            }
            syndicateInfo["cardType"] = "Managed";
            return syndicateInfo;
          })
        )
        .subscribe(
          (syndicateInfo) => {
            showTicket(syndicateInfo);
          },
          (err) => {
            return err;
          }
        );
    } else {
      this.authService.getConstantsMsg("1015").then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "something went wrong!",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      });
    }
  }

  // This function is used to set syndicate pending in user collection to display pending animation
  setSyndicatePending(): Observable<void | any> {
    return this.commonService.getUid("SyndicateService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return EMPTY;
        }

        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { syndicate_pending: true };
        const method = "update";

        return from(this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        })).pipe(
          catchError((err) => of(err))
        );
      })
    );
  }

  // Clears syndicate pending once active syndicates are available on the list
  clearSyndicatePending(): Observable<void | any> {
    return this.commonService.getUid("SyndicateService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return EMPTY;
        }

        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { syndicate_pending: false };
        const method = "update";

        return from(this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        })).pipe(
          catchError((err) => of(err))
        );
      })
    );
  }

  showPrizeDrawToastMessage(): void {
    this.authService.getConstantsMsg("1122").then((translatedMsg) => {
      const defaultMsg = {
        message: {
          line_1: "Congratulations – You’ve upgraded and in the draw",
          line_2: "",
        },
        style: {
          icon: "tick circle success.png",
          type: "Success",
        },
      };
      this.authService.showToastMessage(translatedMsg, defaultMsg);
    });
  }

  /**
   * The function `trackCustomerPurchaseForCheckout` retrieves customer purchase details and processes
   * the payment status for checkout, triggering success or error flows accordingly.
   */
  trackCustomerPurchaseForCheckout(
    cpId: string,
    pathString: string,
    successFlow: () => void,
    errorFlow: () => void
  ) {
    this.commonService.getUid("CheckoutService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        return this.getCustomerPurchaseDetailsFromEncryptedCpId(cpId).pipe(
          takeUntil(this.unsubscribe$),
          map((data: Data | null) => ({ userId, data }))
        );
      })
    ).subscribe(({ userId, data }) => {
      if (!data) return;

      const purchaseDoc = data?.payload?.data();
      const processed_status = purchaseDoc?.processed_status;

      if (processed_status !== undefined) {
        if (processed_status === 2) {
          successFlow();
          this.emitSyndicatePurchaseComplete();
          this.authService.getConstantsMsg("1092").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Payment has been successfully processed",
                line_2: "",
              },
              style: {
                icon: "tick circle success.png",
                type: "Success",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          });

          purchaseDoc.show_toast = true;

          const path_id = `JL4LGZDCJv0wpAfFrAPc`;
          const doc_data = { payment_response: purchaseDoc };
          const dv = { docId: pathString };
          const method = "update";

          this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
          }).subscribe({
            error: (err) => {
              return err;
            }
          });
        } else if (processed_status === -1) {
          errorFlow();
        }
      }
    });
  }


  // This method is for get offer details attributes
  formPaymentDataForTopUp(offer: Data) {
    return {
      prosub_id: offer?.prosub_id,
      phone: offer?.phone,
      prize: offer?.price,
      offers: offer,
      checkout: true,
      draw: "",
      game: "",
      line: "",
      // Prosub offers use `offerid`; keep `offer_id` for callers that expect underscore form.
      offer_id: offer?.offer_id ?? offer?.offerid,
      real_name: "",
      page: "store",
    };
  }

  showPaymentStatusToastMessage(payment_status: Data): void {
    try {
      const description = payment_status?.result?.description?.toLowerCase();
      if (description && (description.search("successfully") > -1 || description.search("succeeded") > -1)) {
        this.emitSyndicatePurchaseComplete();
        this.authService
          .getConstantsMsg("1145")
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Transaction succeeded",
                line_2: "",
              },
              style: {
                icon: "tick circle success.png",
                type: "Success",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          })
          .catch((error) => error);
      } else {
        this.authService
          .getConstantsMsg("1014")
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong!",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          })
          .catch((error) => error);
      }
    } catch (error) {
      return error;
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Loads customer-purchase error details and presents payment feedback.
   *
   * @param cpId Encrypted customer purchase id.
   * @param onStaticFeedback Optional UI hook. When supplied, the default
   *   `authService.showToastMessage` call is skipped and the caller must render or
   *   persist feedback (e.g. a 4C static alert). If the callback throws, feedback falls
   *   back to `showToastMessage`. Redirect / teardown for `processed_status === -1` always
   *   runs afterward and is not blocked by feedback failures.
   */
  paymentErrorMessage(
    cpId: string,
    onStaticFeedback?: (
      translatedMsg: Data,
      defaultMsg: Data,
      approvedCase: boolean,
      errorRes: Data
    ) => void
  ) {
    let errorStatus = true;
    this.customerPurchaseSubscription = this.getCustomerPurchaseDetailsFromEncryptedCpId(cpId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: Data) => {
        const errorRes = data;
        const errorMessage = errorRes?.payment_response_description;
        if (errorMessage && errorStatus) {
          errorStatus = false;

          const lowercaseErrorMessage = errorMessage.toLowerCase();
          const approvedCase =
            lowercaseErrorMessage?.search(this.approvedMessageString) > -1 ||
            lowercaseErrorMessage?.search(this.succeededMessageString) > -1;

          const translatedMsg = {
            message: {
              line_1: errorMessage,
              line_2: "",
            },
            style: {
              icon: "tick circle" + (approvedCase ? " success.png" : " error.svg"),
              type: approvedCase ? "Success" : "Error",
            },
          };

          const defaultMsg = {
            message: {
              line_1: "There is an issue in processing payment",
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };

          this.presentPaymentErrorFeedback(
            translatedMsg,
            defaultMsg,
            approvedCase,
            errorRes,
            onStaticFeedback
          );

          if (!approvedCase && errorRes?.processed_status === -1) {
            localStorage.setItem("payType", "cash-issue");
            setTimeout(() => {
              this.storeService.triggerCloseModal();
            }, 1000);
          }
          this.customerPurchaseSubscription?.unsubscribe();
        }
      });
  }

  /**
   * Shows payment-error feedback via toast or an optional static UI callback.
   * Failures here do not propagate to `paymentErrorMessage` post-feedback handling.
   */
  private presentPaymentErrorFeedback(
    translatedMsg: Data,
    defaultMsg: Data,
    approvedCase: boolean,
    errorRes: Data,
    onStaticFeedback?: (
      translatedMsg: Data,
      defaultMsg: Data,
      approvedCase: boolean,
      errorRes: Data
    ) => void
  ): void {
    try {
      if (onStaticFeedback) {
        onStaticFeedback(translatedMsg, defaultMsg, approvedCase, errorRes);
      } else {
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      }
    } catch (feedbackError) {
      this.authService.errorHandlerLogs(
        "paymentErrorMessage: onStaticFeedback failed, falling back to toast",
        feedbackError
      );
      try {
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      } catch (toastError) {
        this.authService.errorHandlerLogs(
          "paymentErrorMessage: showToastMessage fallback failed",
          toastError
        );
      }
    }
  }

  trackPaymentStatus(cpId: string, eventbody: Data): void {
    this.getCustomerPurchaseDetailsFromEncryptedCpId(cpId)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((data: Data) => data?.payload?.data() as { processed_status?: number }),
        filter((purchaseDoc) => purchaseDoc?.processed_status === 2),
        take(1)
      )
      .subscribe(() => {
        this.mixPanelService.salesGerneryMixpanel("Store Organic Cash", eventbody);
        this.mixPanelService.GTMPopupInitializationForInboxNew("Order Success", eventbody);
      });
  }
  insertCallCenterDirectDebitDetails(debitDetails) {
    return this.commonService.getUid("DirectDebitService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        const payload = {
          module_name: "insert_call_center_direct_debit",
          user_id: userId, // ✅ from commonService
          account_holder_name: debitDetails?.cardholder_name,
          bank_sortcode: debitDetails?.sort_code,
          account_number: debitDetails?.account_number,
          amount: debitDetails?.amount,
          offer_id: debitDetails?.offer_id,
          prosub_id: debitDetails?.prosub_id,
          tp1: debitDetails?.tp1 ?? debitDetails?.TP1,
          tp2: debitDetails?.tp2 ?? debitDetails?.TP2,
          other: debitDetails?.other || debitDetails?.Other || localStorage.getItem("Other") || "",
          address_1: debitDetails?.address_1,
          address_2: debitDetails?.address_2,
          town: debitDetails?.town,
          postal_code: debitDetails?.post_code,
          calling_method: "db",
          p_type: debitDetails.p_type ?? "16",
          agent_id: "55",
        };

        return this.authService.getV2CloudFunction("createdirectdebitv2", payload);
      })
    );
  }


  getpostcodeaddresslookupDetails(postCode) {
    return this.authService.getV2CloudFunction("postcodeaddresslookupv2", { postcode: postCode });
  }

  /** SQL-synced letter toast_id (A–K) → Firebase toast_message/list doc id. */
  private static readonly fourCToastLetterToDocId: Record<string, string> = {
    A: "1166",
    B: "1167",
    C: "1168",
    D: "1169",
    E: "1170",
    F: "1171",
    G: "1172",
    H: "1173",
    I: "1174",
    J: "1175",
    K: "1176",
  };

  /** Parses `payment_status` when SQL stores it as a JSON string. */
  private parseFourCPaymentStatusField(paymentStatus: Data): Data | null {
    let paymentStatusObj = paymentStatus?.payment_status;
    if (typeof paymentStatusObj === "string") {
      try {
        paymentStatusObj = JSON.parse(paymentStatusObj);
      } catch {
        paymentStatusObj = null;
      }
    }
    return paymentStatusObj && typeof paymentStatusObj === "object" ? paymentStatusObj : null;
  }

  /** toast_id from SQL → Firebase customer_purchase. */
  extractFourCPaymentToastId(paymentStatus: Data): string | null {
    const paymentStatusObj = this.parseFourCPaymentStatusField(paymentStatus);

    const raw =
      paymentStatus?.toast_id ??
      paymentStatus?.toastId ??
      paymentStatus?.payment_response?.toast_id ??
      paymentStatus?.payload?.toast_id ??
      paymentStatus?.response?.toast_id ??
      paymentStatusObj?.toast_id ??
      paymentStatusObj?.toastId;

    return this.normalizeFourCToastDocId(raw);
  }

  /** Maps letter toast_id (A–K) or whitelisted numeric id to Firebase toast_message doc id. */
  normalizeFourCToastDocId(raw: unknown): string | null {
    if (raw === null || raw === undefined || raw === "") return null;
    const s = String(raw).trim();
    if (!s) return null;

    const letterKey = s.toUpperCase();
    if (PaymentService.fourCToastLetterToDocId[letterKey]) {
      return PaymentService.fourCToastLetterToDocId[letterKey];
    }

    if (/^\d+$/.test(s) && PaymentService.fourCKnownToastDocIds.has(s)) {
      return s;
    }

    return null;
  }

  /** True when SQL synced a known toast_id (success or failure). */
  hasFourCPaymentFeedback(paymentStatus: Data): boolean {
    return !!this.extractFourCPaymentToastId(paymentStatus);
  }

  /** True when synced feedback exists and does not represent a successful payment. */
  hasFourCPaymentFailureFeedback(paymentStatus: Data): boolean {
    const docId = this.extractFourCPaymentToastId(paymentStatus);
    if (!docId) {
      return false;
    }
    if (docId === PaymentService.fourCSuccessToastId) {
      return false;
    }
    return !this.isFourCPaymentSuccessStatus(paymentStatus);
  }

  private isFourCPaymentSuccessStatus(paymentStatus: Data): boolean {
    if (paymentStatus?.processed_status === 2) {
      return true;
    }

    const topLevelStatus = String(paymentStatus?.payment_status ?? "").toLowerCase();
    if (topLevelStatus === "success") {
      return true;
    }

    const parsed = this.parseFourCPaymentStatusField(paymentStatus);
    if (!parsed) {
      return false;
    }

    const nestedStatus = String(
      parsed.payment_status ?? parsed.status ?? parsed.result?.status ?? ""
    ).toLowerCase();

    return (
      nestedStatus === "success" ||
      nestedStatus === this.succeededMessageString ||
      nestedStatus === this.approvedMessageString
    );
  }

  /**
   * Resolves toast_message doc id from SQL-synced toast_id.
   * Localized copy via getConstantsMsg(docId) using the user's preferred language.
   */
  resolveFourCPaymentFeedbackDocId(paymentStatus: Data): string {
    const syncedToastId = this.extractFourCPaymentToastId(paymentStatus);
    if (syncedToastId) {
      return syncedToastId;
    }
    if (this.isFourCPaymentSuccessStatus(paymentStatus)) {
      return PaymentService.fourCSuccessToastId;
    }
    return PaymentService.fourCDefaultToastId;
  }

  /** @deprecated Use resolveFourCPaymentFeedbackDocId */
  mapFourCPaymentFeedbackDocId(paymentStatus: Data): string {
    return this.resolveFourCPaymentFeedbackDocId(paymentStatus);
  }

  mapFourCPaymentFeedbackFallback(docId: string): ToastPayload {
    const safeDocId = PaymentService.fourCKnownToastDocIds.has(docId)
      ? docId
      : PaymentService.fourCDefaultToastId;
    const isSuccess = safeDocId === PaymentService.fourCSuccessToastId;
    const type: ToastSeverity = isSuccess ? "success" : "error";
    return {
      code: safeDocId,
      message: { line_1: "", line_2: "" },
      style: {
        type,
        icon: isSuccess ? "tick circle success.png" : "tick circle error.svg",
        duration_ms: 0,
      },
    };
  }

  mapPaymentReasonCode(paymentStatus: Data): string {
    const status = paymentStatus?.payment_response?.payment_status || paymentStatus?.result?.status || "";
    const description = paymentStatus?.result?.description || "";
  
    if (status === "success") return "payment-success";
    if (description.toLowerCase().includes("add new card")) return "card-retry-required";
    if (description.toLowerCase().includes("processing payment")) return "payment-processing-failed";
    return "payment-generic-failure";
  }
  
  mapPaymentToastCode(paymentStatus: Data): string {
    const reason = this.mapPaymentReasonCode(paymentStatus);
    if (reason === "payment-success") return "1158";
    if (reason === "card-retry-required") return "1049";
    if (reason === "payment-processing-failed") return "1050";
    return "1015";
  }

  /**
   * Maps a fourcpaymentv2 response and current identity snapshot into resolver inputs.
   */
  buildFourCStateInputFromResponse(
    fourCResponse: FourCPaymentV2StateFields | Data | null,
    options: {
      isAnonymous?: boolean;
      signupStatus?: string | null;
      userId?: string | null;
      anonymousId?: string | null;
      customerId?: string | null;
      checkoutAttemptId?: string | null;
    } = {}
  ): FourCJourneyStateInput {
    const identity = this.salesJourneyIdentityService.getSalesJourneyIdentity({
      userId: options.userId ?? undefined,
      anonymousId: options.anonymousId ?? undefined,
      customerId: options.customerId ?? undefined,
      checkoutAttemptId: options.checkoutAttemptId ?? undefined,
    });

    return {
      userId: identity.userId,
      customerId: identity.customerId,
      anonymousId: identity.anonymousId,
      checkoutAttemptId: identity.checkoutAttemptId,
      reconciliationStatus: this.extractFourCReconciliationStatus(fourCResponse),
      paymentEligibility: this.extractFourCPaymentEligibility(fourCResponse),
      isAnonymous: options.isAnonymous ?? false,
      purchaseCappingGoodForPayment: fourCResponse?.purchaseCapping?.goodForPayment ?? null,
      hasPaymentCappingSyndicate: !!fourCResponse?.purchaseCapping?.paymentCappingSyndicateId,
      signupStatus: options.signupStatus ?? null,
    };
  }

  private extractFourCReconciliationStatus(
    fourCResponse: FourCPaymentV2StateFields | Data | null
  ): string | null {
    if (!fourCResponse) {
      return null;
    }
    return (
      fourCResponse.reconciliationStatus ??
      fourCResponse.reconciliation_status ??
      fourCResponse.identity?.reconciliation_status ??
      null
    );
  }

  private extractFourCPaymentEligibility(
    fourCResponse: FourCPaymentV2StateFields | Data | null
  ): FourCJourneyStateInput["paymentEligibility"] {
    if (!fourCResponse) {
      return null;
    }

    const explicit =
      fourCResponse.paymentEligibility ??
      fourCResponse.payment_eligibility ??
      fourCResponse.identity?.payment_eligibility;

    if (explicit != null && explicit !== "") {
      return explicit;
    }

    return this.deriveFourCPaymentEligibility(fourCResponse);
  }

  private deriveFourCPaymentEligibility(
    fourCResponse: FourCPaymentV2StateFields | Data
  ): FourCJourneyStateInput["paymentEligibility"] {
    if (
      fourCResponse?.purchaseCapping?.paymentCappingSyndicateId &&
      fourCResponse?.purchaseCapping?.goodForPayment === false
    ) {
      return "blocked";
    }
    return null;
  }

  mapPaymentToastFallback(paymentStatus: Data): ToastPayload {
    const status =
      paymentStatus?.payment_response?.payment_status ||
      paymentStatus?.result?.status ||
      "";
  
    const description =
      paymentStatus?.result?.description || "Something went wrong";
  
    const isSuccess = status === "success";
  
    const type: ToastSeverity = isSuccess ? "success" : "error"; // ✅ important
  
    return {
      code: "",
      message: {
        line_1: isSuccess ? "Payment successful!" : description,
        line_2: "",
      },
      style: {
        type, // ✅ now correctly typed
        icon: isSuccess
          ? "tick circle success.png"
          : "error circle.png",
        duration_ms: 2000,
      },
    };
  }

  /**
   * Live customer_purchase snapshots (add_on + syndicate id). Does not complete after the first emit.
   */
  watchCustomerPurchaseSnapshot(encryptCustomerPurchaseId?: string): Observable<Data | null> {
    return (this.commonService.getUid("") || of(null)).pipe(
      switchMap((userId) => {
        if (!userId || !encryptCustomerPurchaseId) {
          return of(null);
        }
        return docData(
          doc(this.firestore, `users/${userId}/customer_purchase/${encryptCustomerPurchaseId}`)
        ).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          map((snapshot) => snapshot ?? null),
          catchError((error) => {
            this.authService.errorHandlerLogs("watchCustomerPurchaseSnapshot error", error);
            return of(null);
          })
        );
      }),
      catchError((error) => {
        this.authService.errorHandlerLogs("watchCustomerPurchaseSnapshot error", error);
        return of(null);
      })
    );
  }

  /** Live managed_tickets/{id}/products. Completes only when the caller unsubscribes. */
  watchSyndicateProducts(syndicateId: string): Observable<Data[]> {
    return (this.commonService.getUid("") || of(null)).pipe(
      switchMap((userId) => {
        if (!userId || !syndicateId || syndicateId === "0") {
          return of([]);
        }
        const productsRef = collection(
          this.firestore,
          `users/${userId}/managed_tickets/${syndicateId}/products`
        );
        const q = query(productsRef, orderBy("product_name", "desc"));
        return collectionData(q, { idField: "id" }).pipe(
          map((products) => (Array.isArray(products) ? products : [])),
          catchError((error) => {
            this.authService.errorHandlerLogs("watchSyndicateProducts error", error);
            return of([]);
          })
        );
      }),
      catchError((error) => {
        this.authService.errorHandlerLogs("watchSyndicateProducts error", error);
        return of([]);
      })
    );
  }

  /**
   * Watch the customer_purchase doc for encrypt_syndicate_id.
   * Returns null when unavailable; errors/timeouts are logged and swallowed to keep payment stable.
   */
  watchCustomerPurchaseDoc(encryptCustomerPurchaseId?: string): Observable<string | null> {
    return (this.commonService.getUid('') || of(null)).pipe(
      switchMap((userId) => {
        if (!userId) {
          return of(null);
        }
        if (!encryptCustomerPurchaseId) {
          this.authService.errorHandlerLogs('Encrypt syndicate id is required');
          return of(null);
        }
        return docData(
          doc(this.firestore, `users/${userId}/customer_purchase/${encryptCustomerPurchaseId}`)
        ).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          catchError((error) => {
            this.authService.errorHandlerLogs('watchCustomerPurchaseDoc docData error', error);
            return of(null);
          }),
          map((data: Data | null) => {
            const encryptSyndicateId = data?.api_response?.response?.encrypt_syndicate_id ?? null;
            if (encryptSyndicateId) {
              this.getCustomerPurchaseDetails$.next(data);
            }
            return encryptSyndicateId;
          }),
          filter((encryptSyndicateId): encryptSyndicateId is string => !!encryptSyndicateId),
          take(1),
          // Align with the shared post-payment clock (remaining ms), not a fresh 15s.
          timeout({ each: Math.max(1, this.remainingPostPaymentTicketMs()) }),
          catchError((error) => {
            if (error?.name !== "TimeoutError") {
              this.authService.errorHandlerLogs("watchCustomerPurchaseDoc error", error);
            }
            return of(null);
          }),
          defaultIfEmpty(null)
        );
      }),
      catchError((error) => {
        this.authService.errorHandlerLogs('watchCustomerPurchaseDoc error', error);
        return of(null);
      })
    );
  }
}
